import { createServerFn } from '@tanstack/react-start';
import { createClient } from '@supabase/supabase-js';
import { z } from 'zod';
import { requireSupabaseAuth } from '@/lib/require-auth';
import { videoInput, VIDEO_BUCKET, hasExcludedVideoMedia, type PublicTourVideo } from './tour-video';

export const listPublicTourVideos = createServerFn({ method: 'GET' }).handler(async (): Promise<PublicTourVideo[]> => {
  const key = process.env['SUPABASE_PUBLISHABLE_KEY'];
  const url = process.env['SUPABASE_URL'];
  if (!key || !url) return [];
  const sb = createClient(url, key, { auth: { persistSession: false, autoRefreshToken: false }, global: { fetch: (input, init) => {
    const headers = new Headers(init?.headers);
    if (key.startsWith('sb_') && headers.get('Authorization') === `Bearer ${key}`) headers.delete('Authorization');
    headers.set('apikey', key); return fetch(input, { ...init, headers });
  } } });
  const { data, error } = await sb.from('tour_videos').select('id,title,destination,clip_path,full_path,poster_path,sort_order,tour_video_tours(product_id)').eq('status', 'published').order('sort_order').limit(200);
  if (error) throw new Error('No se pudieron cargar los videos.');
  if (!data?.length) return [];
  const allowed = data.filter(v => !hasExcludedVideoMedia(v));
  if (!allowed.length) return [];
  const paths = [...new Set(allowed.flatMap(v => [v.clip_path, v.full_path, v.poster_path].filter((p): p is string => !!p)))];
  const signed = await sb.storage.from(VIDEO_BUCKET).createSignedUrls(paths, 3600);
  if (signed.error) throw new Error('No se pudieron cargar los videos.');
  const urls = new Map((signed.data ?? []).filter(p => !!p.signedUrl).map(p => [p.path, p.signedUrl?.replace(url, import.meta.env.VITE_SUPABASE_URL)]));
  return allowed.map(v => ({ id: v.id, title: v.title, destination: v.destination, sort_order: v.sort_order,
    clip: urls.get(v.clip_path) ?? null, full: urls.get(v.full_path) ?? null, poster: urls.get(v.poster_path) ?? null,
    productIds: (v.tour_video_tours ?? []).map((t: { product_id: string }) => t.product_id),
  }));
});

export const listAdminTourVideos = createServerFn({ method: 'POST' }).middleware([requireSupabaseAuth]).handler(async ({ context }) => {
  const { requireAdminMfa } = await import('./staff-gate.server'); await requireAdminMfa(context, 'videos_read');
  const { data, error } = await context.supabase.from('tour_videos').select('*,tour_video_tours(product_id)').order('sort_order');
  if (error) throw new Error('No se pudo leer la biblioteca.');
  const { data: bucket, error: bucketError } = await context.supabase.storage.getBucket(VIDEO_BUCKET);
  return { videos: (data ?? []).filter(v => !hasExcludedVideoMedia(v)), maxBytes: bucketError ? null : bucket?.file_size_limit ?? null };
});

export const prepareTourVideoUpload = createServerFn({ method: 'POST' }).middleware([requireSupabaseAuth])
  .inputValidator(z.object({ id: z.string().uuid(), kind: z.enum(['clip','full','poster']), contentType: z.enum(['video/mp4','video/webm','image/jpeg','image/png','image/webp']), size: z.number().positive() }))
  .handler(async ({ context, data }) => {
    const { requireAdminMfa } = await import('./staff-gate.server'); await requireAdminMfa(context, 'videos_upload');
    if ((data.kind === 'poster') !== data.contentType.startsWith('image/')) throw new Error('Tipo de archivo incorrecto.');
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server');
    const { data: bucket, error: bucketError } = await supabaseAdmin.storage.getBucket(VIDEO_BUCKET);
    if (bucketError) throw new Error('Almacenamiento de videos no disponible.');
    if (bucket.file_size_limit && data.size > bucket.file_size_limit) throw new Error(`El archivo supera el límite de ${Math.floor(bucket.file_size_limit/1048576)} MB.`);
    const ext = data.contentType.split('/')[1];
    const path = `${data.id}/${context.userId}/${data.kind}-${crypto.randomUUID()}.${ext}`;
    const { data: signed, error } = await context.supabase.storage.from(VIDEO_BUCKET).createSignedUploadUrl(path);
    if (error || !signed) throw new Error('No se pudo preparar la subida.');
    return { path, token: signed.token };
  });

export const saveTourVideo = createServerFn({ method: 'POST' }).middleware([requireSupabaseAuth]).inputValidator(videoInput)
  .handler(async ({ context, data }) => {
    const { requireAdminMfa } = await import('./staff-gate.server'); await requireAdminMfa(context, 'videos_save');
    const { assertPublicBokunId } = await import('./public-catalog-guard.server');
    await Promise.all(data.productIds.map(id => assertPublicBokunId(id)));
    for (const path of [data.clip_path,data.full_path,data.poster_path]) {
      if (!path) continue;
      if (!path.startsWith(`${data.id}/`) || path.includes('..')) throw new Error('Archivo fuera de esta entrada.');
      const slash = path.lastIndexOf('/');
      const { data: objects, error } = await context.supabase.storage.from(VIDEO_BUCKET).list(path.slice(0,slash), { search: path.slice(slash+1) });
      if (error || !objects?.some(o => o.name === path.slice(slash+1))) throw new Error('Completa la subida antes de guardar.');
    }
    const { error } = await context.supabase.rpc('save_tour_video', { payload: data });
    if (error) throw new Error('No se pudo guardar el video y sus asignaciones.');
    return { ok: true };
  });